import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  deleteDoc,
  limit
} from 'firebase/firestore';
import { DOCUMENT_TEMPLATES } from '../components/LegalDocumentTemplates';

// Mapping entre les types de documents client et les types de documents génériques
const DOCUMENT_TYPE_MAPPING = {
  'MANDAT': 'CONTRAT_MANDAT',
  'CGS': 'CGS',
  'LIVRET_ACCUEIL': 'LIVRET_ACCUEIL',
  'CHARTE_DROITS': 'CHARTE_DROITS',
  'REGLEMENT_BENEFICIAIRES': 'REGLEMENT_INTERIEUR',
  'REGLEMENT_INTERVENANTS': 'REGLEMENT_INTERIEUR',
  'CONTRAT_TRAVAIL': 'CONTRAT_TRAVAIL',
  'ATTESTATION_FISCALE': 'ATTESTATION_FISCALE'
};

/**
 * Service de gestion des documents pour les bénéficiaires
 * Gère la génération, le stockage et le workflow des documents personnalisés
 */

// Fonction pour remplir un template avec les données du bénéficiaire
const fillDocumentTemplate = (template, beneficiaryData, additionalData = {}) => {
  let filledContent = template;

  // Données du bénéficiaire
  const client = beneficiaryData || {};

  // Données additionnelles (contrat, intervenant, etc.)
  const { contrat, intervenant, companyInfo, periode, montants } = additionalData;

  // === Support pour la nouvelle syntaxe {{placeholder}} (Documents Génériques) ===

  // Remplacer les placeholders pour le bénéficiaire/client (nouvelle syntaxe)
  filledContent = filledContent.replace(/\{\{beneficiary\.firstName\}\}/g, client.firstName || '______');
  filledContent = filledContent.replace(/\{\{beneficiary\.lastName\}\}/g, client.lastName || '______');
  filledContent = filledContent.replace(/\{\{beneficiary\.fullName\}\}/g, `${client.firstName || '______'} ${client.lastName || '______'}`);
  filledContent = filledContent.replace(/\{\{beneficiary\.address\}\}/g, client.address || '______');
  filledContent = filledContent.replace(/\{\{beneficiary\.phone\}\}/g, client.phone || client.phoneNumber || '______');
  filledContent = filledContent.replace(/\{\{beneficiary\.email\}\}/g, client.email || '______');

  // Alias client.* pour la nouvelle syntaxe
  filledContent = filledContent.replace(/\{\{client\.firstName\}\}/g, client.firstName || '______');
  filledContent = filledContent.replace(/\{\{client\.lastName\}\}/g, client.lastName || '______');
  filledContent = filledContent.replace(/\{\{client\.fullName\}\}/g, `${client.firstName || '______'} ${client.lastName || '______'}`);
  filledContent = filledContent.replace(/\{\{client\.address\}\}/g, client.address || '______');
  filledContent = filledContent.replace(/\{\{client\.phone\}\}/g, client.phone || client.phoneNumber || '______');
  filledContent = filledContent.replace(/\{\{client\.email\}\}/g, client.email || '______');

  // Remplacer les placeholders pour l'entreprise (nouvelle syntaxe)
  const defaultCompanyName = 'OMAD Services';
  const defaultCompanyAddress = '30 bis Rue des Bondoires, 18230 Saint-Doulchard';
  filledContent = filledContent.replace(/\{\{company\.name\}\}/g, companyInfo?.name || defaultCompanyName);
  filledContent = filledContent.replace(/\{\{company\.address\}\}/g, companyInfo?.address || defaultCompanyAddress);

  // Remplacer les placeholders de date (nouvelle syntaxe)
  const today = new Date().toLocaleDateString('fr-FR');
  filledContent = filledContent.replace(/\{\{date\.today\}\}/g, today);
  filledContent = filledContent.replace(/\{\{date\.current\}\}/g, today);

  // === Support pour l'ancienne syntaxe ${} (rétrocompatibilité) ===

  // Remplacer les placeholders pour le client/bénéficiaire (ancienne syntaxe)
  filledContent = filledContent.replace(/\$\{client\?\.firstName \|\| '______'\}/g, client.firstName || '______');
  filledContent = filledContent.replace(/\$\{client\?\.lastName \|\| '______'\}/g, client.lastName || '______');
  filledContent = filledContent.replace(/\$\{client\?\.address \|\| '______'\}/g, client.address || '______');
  filledContent = filledContent.replace(/\$\{client\?\.phone \|\| '______'\}/g, client.phone || '______');
  filledContent = filledContent.replace(/\$\{client\?\.email \|\| '______'\}/g, client.email || '______');

  // Remplacer les placeholders pour le contrat
  if (contrat) {
    filledContent = filledContent.replace(/\$\{contrat\?\.startDate \|\| '___ \/ ___ \/ _____'\}/g, contrat.startDate || '___ / ___ / _____');
    filledContent = filledContent.replace(/\$\{contrat\?\.endDate\}/g, contrat.endDate || '');
    filledContent = filledContent.replace(/\$\{contrat\?\.durationType === 'CDD' \? 'Déterminée \(CDD\)' : 'Indéterminée \(CDI\)'\}/g, contrat.durationType === 'CDD' ? 'Déterminée (CDD)' : 'Indéterminée (CDI)');
    filledContent = filledContent.replace(/\$\{contrat\?\.weeklyHours \|\| '___'\}/g, contrat.weeklyHours || '___');
    filledContent = filledContent.replace(/\$\{contrat\?\.hourlyRateBrut \|\| '____'\}/g, contrat.hourlyRateBrut || '____');
    filledContent = filledContent.replace(/\$\{contrat\?\.hourlyRateNet \|\| '____'\}/g, contrat.hourlyRateNet || '____');
    filledContent = filledContent.replace(/\$\{contrat\?\.trialPeriod \|\| '___'\}/g, contrat.trialPeriod || '___');

    // Nouvelle syntaxe pour le contrat
    filledContent = filledContent.replace(/\{\{contract\.startDate\}\}/g, contrat.startDate || '___ / ___ / _____');
    filledContent = filledContent.replace(/\{\{contract\.endDate\}\}/g, contrat.endDate || '______');
    filledContent = filledContent.replace(/\{\{contract\.weeklyHours\}\}/g, contrat.weeklyHours || '___');
    filledContent = filledContent.replace(/\{\{contract\.hourlyRateBrut\}\}/g, contrat.hourlyRateBrut || '____');
    filledContent = filledContent.replace(/\{\{contract\.hourlyRateNet\}\}/g, contrat.hourlyRateNet || '____');
  }

  // Remplacer les placeholders pour l'intervenant
  if (intervenant) {
    filledContent = filledContent.replace(/\$\{intervenant\?\.firstName \|\| '______'\}/g, intervenant.firstName || '______');
    filledContent = filledContent.replace(/\$\{intervenant\?\.lastName \|\| '______'\}/g, intervenant.lastName || '______');
    filledContent = filledContent.replace(/\$\{intervenant\?\.birthDate \|\| '____ \/ ____ \/ ______'\}/g, intervenant.birthDate || '____ / ____ / ______');
    filledContent = filledContent.replace(/\$\{intervenant\?\.birthPlace \|\| '______'\}/g, intervenant.birthPlace || '______');
    filledContent = filledContent.replace(/\$\{intervenant\?\.address \|\| '______'\}/g, intervenant.address || '______');
    filledContent = filledContent.replace(/\$\{intervenant\?\.socialSecurity \|\| '______'\}/g, intervenant.socialSecurity || '______');

    // Nouvelle syntaxe pour l'intervenant
    filledContent = filledContent.replace(/\{\{intervenant\.firstName\}\}/g, intervenant.firstName || '______');
    filledContent = filledContent.replace(/\{\{intervenant\.lastName\}\}/g, intervenant.lastName || '______');
    filledContent = filledContent.replace(/\{\{intervenant\.fullName\}\}/g, `${intervenant.firstName || '______'} ${intervenant.lastName || '______'}`);
    filledContent = filledContent.replace(/\{\{intervenant\.address\}\}/g, intervenant.address || '______');
  }

  // Remplacer les placeholders pour les infos de l'entreprise (ancienne syntaxe)
  if (companyInfo) {
    filledContent = filledContent.replace(/\$\{companyInfo\?\.name\}/g, companyInfo.name || defaultCompanyName);
    filledContent = filledContent.replace(/\$\{companyInfo\?\.address\}/g, companyInfo.address || defaultCompanyAddress);
  }

  // Remplacer les placeholders pour la période (attestation fiscale)
  if (periode) {
    filledContent = filledContent.replace(/\$\{periode \|\| '______'\}/g, periode);
    filledContent = filledContent.replace(/\{\{period\}\}/g, periode);
  }

  // Remplacer les placeholders pour les montants (attestation fiscale)
  if (montants) {
    Object.keys(montants).forEach(key => {
      const regex = new RegExp(`\\$\\{montants\\?\\.${key} \\|\\| '____'\\}`, 'g');
      filledContent = filledContent.replace(regex, montants[key] || '____');

      // Nouvelle syntaxe
      const newRegex = new RegExp(`\\{\\{amount\\.${key}\\}\\}`, 'g');
      filledContent = filledContent.replace(newRegex, montants[key] || '____');
    });
  }

  // Remplacer la date actuelle (ancienne syntaxe)
  filledContent = filledContent.replace(/\$\{new Date\(\)\.toLocaleDateString\('fr-FR'\)\}/g, today);

  // Gérer les conditions (représentant légal, etc.)
  // TODO: Implémenter la logique conditionnelle si nécessaire

  return filledContent;
};

/**
 * Récupère le template depuis les Documents Génériques
 * @param {Object} db - Instance Firestore
 * @param {string} documentType - Type de document
 * @returns {Promise<string>} - Contenu du template
 */
const getTemplateFromGenericDocuments = async (db, documentType) => {
  try {
    // Mapper le type de document au type générique
    const genericDocType = DOCUMENT_TYPE_MAPPING[documentType] || documentType;

    console.log(`🔍 Recherche du template générique pour: ${documentType} -> ${genericDocType}`);

    // Chercher le document générique correspondant
    const genericDocsRef = collection(db, 'genericDocuments');
    const q = query(
      genericDocsRef,
      where('documentType', '==', genericDocType),
      orderBy('version', 'desc'),
      limit(1)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      console.warn(`⚠️ Aucun template générique trouvé pour ${genericDocType}, utilisation du template codé en dur`);

      // Fallback sur les templates codés en dur si pas de document générique
      const templateFunction = DOCUMENT_TEMPLATES[documentType];
      if (!templateFunction) {
        throw new Error(`Template ${documentType} non trouvé ni dans les Documents Génériques ni dans les templates codés`);
      }

      // Retourner le template codé en dur
      return templateFunction({ client: {} });
    }

    // Récupérer le contenu du template
    const templateDoc = querySnapshot.docs[0];
    const templateData = templateDoc.data();

    console.log(`✅ Template générique trouvé: ${templateDoc.id} (v${templateData.version})`);

    return templateData.content || '';
  } catch (error) {
    console.error('❌ Erreur lors de la récupération du template générique:', error);

    // Fallback sur les templates codés en dur en cas d'erreur
    const templateFunction = DOCUMENT_TEMPLATES[documentType];
    if (templateFunction) {
      console.log(`⚠️ Utilisation du template codé en dur pour ${documentType}`);
      return templateFunction({ client: {} });
    }

    throw error;
  }
};

/**
 * Génère un document pour un bénéficiaire
 * @param {Object} db - Instance Firestore
 * @param {string} beneficiaryId - ID du bénéficiaire
 * @param {string} documentType - Type de document (MANDAT, CGS, etc.)
 * @param {Object} additionalData - Données supplémentaires (contrat, intervenant, etc.)
 * @param {string} userId - ID de l'utilisateur créateur
 * @returns {Promise<Object>} - Document généré
 */
export const generateDocumentForBeneficiary = async (
  db,
  beneficiaryId,
  documentType,
  additionalData = {},
  userId
) => {
  try {
    // Récupérer les données du bénéficiaire
    const beneficiaryRef = doc(db, 'clients', beneficiaryId);
    const beneficiarySnap = await getDoc(beneficiaryRef);

    if (!beneficiarySnap.exists()) {
      throw new Error('Bénéficiaire non trouvé');
    }

    const beneficiaryData = beneficiarySnap.data();

    // Récupérer le template depuis les Documents Génériques
    const templateContent = await getTemplateFromGenericDocuments(db, documentType);

    // Remplir le template avec les données
    const filledContent = fillDocumentTemplate(templateContent, beneficiaryData, additionalData);

    // Définir le nom du document
    const documentNames = {
      'MANDAT': 'Contrat de Prestation de Mandat',
      'CGS': 'Conditions Générales de Service',
      'CONTRAT_TRAVAIL': 'Contrat de Travail',
      'LIVRET_ACCUEIL': 'Livret d\'Accueil',
      'REGLEMENT_BENEFICIAIRES': 'Règlement Intérieur Bénéficiaires',
      'REGLEMENT_INTERVENANTS': 'Règlement Intérieur Intervenants',
      'ATTESTATION_FISCALE': 'Attestation Fiscale Annuelle',
      'CHARTE_DROITS': 'Charte des Droits et Libertés'
    };

    const documentName = `${documentNames[documentType] || documentType} - ${beneficiaryData.firstName} ${beneficiaryData.lastName}`;

    // Créer l'objet document
    // Note: Firestore n'accepte pas les valeurs undefined, on utilise donc des valeurs par défaut
    const documentData = {
      documentType,
      documentName,
      content: filledContent,
      status: 'draft',
      createdAt: serverTimestamp(),
      createdBy: userId,
      updatedAt: serverTimestamp(),
      beneficiaryData: {
        id: beneficiaryId,
        firstName: beneficiaryData.firstName || '',
        lastName: beneficiaryData.lastName || '',
        address: beneficiaryData.address || '',
        phone: beneficiaryData.phone || beneficiaryData.phoneNumber || '',
        email: beneficiaryData.email || ''
      },
      metadata: {
        version: '1.0',
        generatedFrom: documentType
      }
    };

    return documentData;
  } catch (error) {
    console.error('Erreur lors de la génération du document:', error);
    throw error;
  }
};

/**
 * Sauvegarde un document pour un bénéficiaire
 * @param {Object} db - Instance Firestore
 * @param {string} beneficiaryId - ID du bénéficiaire
 * @param {Object} documentData - Données du document
 * @returns {Promise<string>} - ID du document créé
 */
export const saveBeneficiaryDocument = async (db, beneficiaryId, documentData) => {
  try {
    // Créer une référence de document avec un ID auto-généré
    const documentsRef = collection(db, 'clients', beneficiaryId, 'documents');
    const newDocRef = doc(documentsRef);

    // Sauvegarder le document
    await setDoc(newDocRef, {
      ...documentData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    console.log('Document sauvegardé avec succès:', newDocRef.id);
    return newDocRef.id;
  } catch (error) {
    console.error('Erreur lors de la sauvegarde du document:', error);
    throw error;
  }
};

/**
 * Met à jour le statut d'un document
 * @param {Object} db - Instance Firestore
 * @param {string} beneficiaryId - ID du bénéficiaire
 * @param {string} documentId - ID du document
 * @param {string} newStatus - Nouveau statut (draft, pending_signature, signed, archived)
 * @param {Object} signatureData - Données de signature (optionnel)
 * @returns {Promise<void>}
 */
export const updateDocumentStatus = async (
  db,
  beneficiaryId,
  documentId,
  newStatus,
  signatureData = null
) => {
  try {
    const documentRef = doc(db, 'clients', beneficiaryId, 'documents', documentId);

    const updateData = {
      status: newStatus,
      updatedAt: serverTimestamp()
    };

    // Ajouter les données de signature si fournies
    if (signatureData) {
      updateData.signature = signatureData;
    }

    await updateDoc(documentRef, updateData);
    console.log('Statut du document mis à jour:', newStatus);
  } catch (error) {
    console.error('Erreur lors de la mise à jour du statut:', error);
    throw error;
  }
};

/**
 * Ajoute une signature à un document
 * @param {Object} db - Instance Firestore
 * @param {string} beneficiaryId - ID du bénéficiaire
 * @param {string} documentId - ID du document
 * @param {string} signatureImage - Image de la signature (base64)
 * @param {string} signedBy - Nom du signataire
 * @param {string} ipAddress - Adresse IP du signataire
 * @returns {Promise<void>}
 */
export const addSignature = async (
  db,
  beneficiaryId,
  documentId,
  signatureImage,
  signedBy,
  ipAddress = null
) => {
  try {
    const signatureData = {
      signatureImage,
      signedBy,
      signedAt: serverTimestamp(),
      ipAddress: ipAddress || 'Non disponible'
    };

    // Mettre à jour le document avec la signature et changer le statut
    await updateDocumentStatus(db, beneficiaryId, documentId, 'signed', signatureData);

    console.log('Signature ajoutée avec succès');
  } catch (error) {
    console.error('Erreur lors de l\'ajout de la signature:', error);
    throw error;
  }
};

/**
 * Récupère tous les documents d'un bénéficiaire
 * @param {Object} db - Instance Firestore
 * @param {string} beneficiaryId - ID du bénéficiaire
 * @param {Object} filters - Filtres optionnels (status, documentType)
 * @returns {Promise<Array>} - Liste des documents
 */
export const getBeneficiaryDocuments = async (db, beneficiaryId, filters = {}) => {
  try {
    const documentsRef = collection(db, 'clients', beneficiaryId, 'documents');
    let q = query(documentsRef, orderBy('createdAt', 'desc'));

    // Appliquer les filtres
    if (filters.status) {
      q = query(documentsRef, where('status', '==', filters.status), orderBy('createdAt', 'desc'));
    }

    if (filters.documentType) {
      q = query(documentsRef, where('documentType', '==', filters.documentType), orderBy('createdAt', 'desc'));
    }

    const querySnapshot = await getDocs(q);
    const documents = [];

    querySnapshot.forEach((doc) => {
      documents.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return documents;
  } catch (error) {
    console.error('Erreur lors de la récupération des documents:', error);
    throw error;
  }
};

/**
 * Récupère un document spécifique
 * @param {Object} db - Instance Firestore
 * @param {string} beneficiaryId - ID du bénéficiaire
 * @param {string} documentId - ID du document
 * @returns {Promise<Object>} - Document
 */
export const getDocumentById = async (db, beneficiaryId, documentId) => {
  try {
    const documentRef = doc(db, 'clients', beneficiaryId, 'documents', documentId);
    const documentSnap = await getDoc(documentRef);

    if (!documentSnap.exists()) {
      throw new Error('Document non trouvé');
    }

    return {
      id: documentSnap.id,
      ...documentSnap.data()
    };
  } catch (error) {
    console.error('Erreur lors de la récupération du document:', error);
    throw error;
  }
};

/**
 * Supprime un document
 * @param {Object} db - Instance Firestore
 * @param {string} beneficiaryId - ID du bénéficiaire
 * @param {string} documentId - ID du document
 * @returns {Promise<void>}
 */
export const deleteDocument = async (db, beneficiaryId, documentId) => {
  try {
    const documentRef = doc(db, 'clients', beneficiaryId, 'documents', documentId);
    await deleteDoc(documentRef);
    console.log('Document supprimé avec succès');
  } catch (error) {
    console.error('Erreur lors de la suppression du document:', error);
    throw error;
  }
};

/**
 * Archive un document (change le statut à 'archived')
 * @param {Object} db - Instance Firestore
 * @param {string} beneficiaryId - ID du bénéficiaire
 * @param {string} documentId - ID du document
 * @returns {Promise<void>}
 */
export const archiveDocument = async (db, beneficiaryId, documentId) => {
  try {
    await updateDocumentStatus(db, beneficiaryId, documentId, 'archived');
    console.log('Document archivé avec succès');
  } catch (error) {
    console.error('Erreur lors de l\'archivage du document:', error);
    throw error;
  }
};

/**
 * Compte le nombre de documents par statut pour un bénéficiaire
 * @param {Object} db - Instance Firestore
 * @param {string} beneficiaryId - ID du bénéficiaire
 * @returns {Promise<Object>} - Compteurs par statut
 */
export const getDocumentsCountByStatus = async (db, beneficiaryId) => {
  try {
    const documents = await getBeneficiaryDocuments(db, beneficiaryId);

    const counts = {
      draft: 0,
      pending_signature: 0,
      signed: 0,
      archived: 0,
      total: documents.length
    };

    documents.forEach(doc => {
      if (counts.hasOwnProperty(doc.status)) {
        counts[doc.status]++;
      }
    });

    return counts;
  } catch (error) {
    console.error('Erreur lors du comptage des documents:', error);
    throw error;
  }
};

/**
 * Envoie un document pour signature
 * @param {Object} db - Instance Firestore
 * @param {string} beneficiaryId - ID du bénéficiaire
 * @param {string} documentId - ID du document
 * @returns {Promise<void>}
 */
export const sendForSignature = async (db, beneficiaryId, documentId) => {
  try {
    await updateDocumentStatus(db, beneficiaryId, documentId, 'pending_signature');
    // TODO: Envoyer notification au bénéficiaire (email/SMS)
    console.log('Document envoyé pour signature');
  } catch (error) {
    console.error('Erreur lors de l\'envoi pour signature:', error);
    throw error;
  }
};
